import React, { useState } from "react";
import Layout from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";

/**
 * Admin-E-Mail-Compose: freies Schreiben und Versenden von E-Mails
 * über den konfigurierten SMTP-Server (info@easygutachter.de).
 */
export default function AdminMailCompose() {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/mail/compose", {
        to: to.trim(),
        subject: subject.trim(),
        body_text: body,
      });
      setLastResult(data);
      if (data.delivered) {
        toast.success(`Versendet an ${data.to}`, {
          description: `Modus: ${data.mode.toUpperCase()}`,
        });
        setTo(""); setSubject(""); setBody("");
      } else {
        toast.warning("Nur im Dry-Run — SMTP nicht erreichbar");
      }
    } catch (err) {
      toast.error(apiError(err, "E-Mail konnte nicht versendet werden"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout title="E-Mail schreiben" subtitle="Versand über info@easygutachter.de">
      <div className="max-w-3xl">
        <form onSubmit={send} className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4" data-testid="mail-compose-form">
          <div className="flex items-center gap-2 text-xs text-zinc-500 pb-3 border-b border-zinc-100">
            <Mail className="h-3.5 w-3.5" />
            Absender: <span className="font-semibold text-zinc-900">info@easygutachter.de</span>
            <span className="text-zinc-400 ml-auto">via SMTP mail.agenturserver.de</span>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">An</label>
            <input
              type="email" required value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="empfaenger@example.de"
              className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition"
              data-testid="mail-compose-to"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">Betreff</label>
            <input
              type="text" required value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff der Nachricht"
              className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition"
              data-testid="mail-compose-subject"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">Nachricht</label>
            <textarea
              required value={body} onChange={(e) => setBody(e.target.value)} rows={12}
              placeholder="Schreiben Sie Ihre Nachricht hier …"
              className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition resize-y"
              data-testid="mail-compose-body"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit" disabled={sending}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition disabled:opacity-50"
              data-testid="mail-compose-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Wird versendet …" : "Absenden"}
            </button>
            {lastResult && (
              <div className="text-xs text-zinc-500" data-testid="mail-compose-last-result">
                Letzte: {lastResult.delivered
                  ? <span className="text-emerald-600 font-semibold">✓ zugestellt an {lastResult.to} ({lastResult.mode})</span>
                  : <span className="text-amber-600 font-semibold">Dry-Run</span>}
              </div>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}
